import { Suspense, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';

import { STATION_TYPE } from '~common';
import { UnpinningNoteField } from '~components/atoms';
import { PaddingBox } from '~components/atoms/custom';
import { useQueryGetReason } from '~hooks/reason/useQueryGetReason';
import { UnpinningReasonKey } from '~types';

type ConfirmRelocationStepProps = {
  stationImei: string | undefined;
  stationType: STATION_TYPE | undefined;
  relocationReasonId: string | undefined;
  onChangeRelocationReasonId: (value: string) => void;
  relocationNote: string | undefined;
  onChangeRelocationNote: (value: string) => void;
};

export const ConfirmRelocationStep: React.FC<ConfirmRelocationStepProps> = (
  props,
) => (
  <Suspense
    fallback={
      <Box
        display="flex"
        width="100%"
        height="100%"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress />
      </Box>
    }
  >
    <ConfirmRelocationView {...props} />
  </Suspense>
);

const ConfirmRelocationView: React.FC<ConfirmRelocationStepProps> = ({
  stationImei,
  stationType,
  relocationReasonId,
  onChangeRelocationReasonId,
  relocationNote,
  onChangeRelocationNote,
}) => {
  const relocationReasons = useQueryGetReason({
    page: 0,
    size: 20,
    filter: {
      filterByKeys: [
        UnpinningReasonKey.UNPINNING_REASONS_FOR_STATION_RELOCATION,
      ],
    },
  });

  useEffect(() => {
    const firstReason = relocationReasons.data?.data?.[0];
    if (firstReason && !relocationReasonId) {
      onChangeRelocationReasonId(firstReason.id);
    }
  }, [
    relocationReasons.data?.data,
    relocationReasonId,
    onChangeRelocationReasonId,
  ]);

  return (
    <PaddingBox
      flex={1}
      sx={{ bgcolor: 'background.paper', width: '100%', pt: 0, px: 0 }}
    >
      <Stack gap={3} pb={4} width="100%">
        <Stack gap={1.5}>
          <Typography variant="h6" fontWeight={600}>
            You're relocating
          </Typography>
          <Stack>
            <Typography variant="body2" color="text.primary">
              IMEI: {stationImei || '---'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {stationType || '---'}
            </Typography>
          </Stack>
        </Stack>

        <Stack width="100%" gap={1}>
          <Typography variant="h6" fontWeight={600}>
            Reason to Relocate
          </Typography>

          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={relocationReasonId ?? ''}
              onChange={(e) => onChangeRelocationReasonId(e.target.value)}
            >
              {relocationReasons.data?.data?.map((option) => (
                <FormControlLabel
                  key={option.id}
                  value={option.id}
                  control={<Radio disableRipple />}
                  label={
                    <Typography variant="body2">{option.reason}</Typography>
                  }
                  sx={{
                    display: 'flex',
                    flexDirection: 'row-reverse',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    mx: 0,
                    '& .MuiFormControlLabel-label': {
                      flex: 1,
                      textAlign: 'left',
                    },
                  }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Stack>

        <UnpinningNoteField
          label="Relocate note"
          value={relocationNote}
          onChange={onChangeRelocationNote}
        />
      </Stack>
    </PaddingBox>
  );
};
